import { LocalStorageVariables } from "../../../../common/entities/type";

/**
 * Remembered column filters for the listings that opt in, keyed by
 * `RecordTable`'s `persistKey`.
 *
 * A filtered listing used to lose its filters the moment the user opened one of
 * its records, which is the most common thing to do with it: switching History
 * tabs swaps the routed child, and a detail route replaces the listing
 * outright, so the table's own state was destroyed every time.
 *
 * Everything lives under one localStorage key rather than one key per table, so
 * that logging out is a single `removeItem` — see the teardown lists in
 * `AuthProvider.logout` and `AppApiManager`'s dead-session path, which are
 * duplicated and both have to name it.
 *
 * localStorage rather than Redux because the filters have to survive a reload;
 * it also avoids a trap in the Redux route — `serviceLogicBuilder` and
 * `stepper` have no reset action and outlive the non-redirecting logout used
 * for `?mode=signup`, because that path never reloads the document.
 */

/** `{ start, end }` as `RecordTable` holds it — live `Date`s. */
interface DateRange {
  start: Date | null;
  end: Date | null;
}

export interface RecordFilters {
  valueFilters: Record<string, string[]>;
  dateFilters: Record<string, DateRange>;
}

/** What actually goes to disk: the same shape with dates as ISO strings. */
interface StoredFilters {
  value?: Record<string, string[]>;
  date?: Record<string, { start: string | null; end: string | null }>;
}

type StoredMap = Record<string, StoredFilters>;

const EMPTY: RecordFilters = { valueFilters: {}, dateFilters: {} };

/**
 * Reading and writing are both wrapped: storage throws outright in Safari's
 * private mode, and a half-written value must not take a listing down with it.
 */
const readMap = (): StoredMap => {
  try {
    const raw = localStorage.getItem(LocalStorageVariables.HISTORY_FILTERS);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as StoredMap) : {};
  } catch {
    return {};
  }
};

const writeMap = (map: StoredMap) => {
  try {
    localStorage.setItem(
      LocalStorageVariables.HISTORY_FILTERS,
      JSON.stringify(map)
    );
  } catch {
    // Out of quota or storage blocked — the filters just stop being
    // remembered, which is better than breaking the table.
  }
};

/** An ISO string back to a `Date`, or null if it is not a usable one. */
const reviveDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * The remembered filters for `persistKey`.
 *
 * `knownColumnKeys` prunes selections belonging to columns that no longer
 * exist. Without it a renamed or removed column would leave a filter that
 * matches nothing and cannot be seen — the table would render empty with no
 * visible cause.
 */
export const readRecordFilters = (
  persistKey: string | undefined,
  knownColumnKeys: string[]
): RecordFilters => {
  if (!persistKey) return EMPTY;

  const stored = readMap()[persistKey];
  if (!stored) return EMPTY;

  const known = new Set(knownColumnKeys);
  const valueFilters: Record<string, string[]> = {};
  const dateFilters: Record<string, DateRange> = {};

  Object.entries(stored.value ?? {}).forEach(([columnKey, selected]) => {
    if (!known.has(columnKey) || !Array.isArray(selected)) return;
    const keys = selected.filter((key) => typeof key === "string");
    if (keys.length) valueFilters[columnKey] = keys;
  });

  Object.entries(stored.date ?? {}).forEach(([columnKey, range]) => {
    if (!known.has(columnKey) || !range) return;
    const start = reviveDate(range.start);
    // A range with no start is not a filter — `RecordTable` treats it as off.
    if (!start) return;
    dateFilters[columnKey] = { start, end: reviveDate(range.end) };
  });

  return { valueFilters, dateFilters };
};

/** Stores the filters for `persistKey`, or drops the entry when none are set. */
export const writeRecordFilters = (
  persistKey: string | undefined,
  { valueFilters, dateFilters }: RecordFilters
) => {
  if (!persistKey) return;

  const value: StoredFilters["value"] = {};
  Object.entries(valueFilters).forEach(([columnKey, selected]) => {
    if (selected.length) value[columnKey] = selected;
  });

  const date: StoredFilters["date"] = {};
  Object.entries(dateFilters).forEach(([columnKey, range]) => {
    if (!range?.start) return;
    date[columnKey] = {
      start: range.start.toISOString(),
      end: range.end ? range.end.toISOString() : null,
    };
  });

  const map = readMap();
  if (!Object.keys(value).length && !Object.keys(date).length) {
    // Nothing filtered: leave no entry behind rather than an empty one.
    delete map[persistKey];
  } else {
    map[persistKey] = { value, date };
  }
  writeMap(map);
};

/**
 * Forgets one listing's filters — what "Clear Filters" persists.
 *
 * Only this key, so clearing the Assessments tab leaves Programming's filters
 * alone.
 */
export const clearRecordFilters = (persistKey: string | undefined) => {
  if (!persistKey) return;
  const map = readMap();
  if (!(persistKey in map)) return;
  delete map[persistKey];
  writeMap(map);
};
